import t from "@/lang/lang";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { FieldErrors } from "@/shared/components/field/field";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import {
  ServiceVariation,
  serviceVariationFromSelectValue,
  serviceVariationSelectItems,
  serviceVariationToSelectValue,
} from "../types/service-variation";

/**
 * ServiceVariationSelect selects a service variation of the service kind, or
 * none. It is disabled for a service kind without service variations.
 */
export function ServiceVariationSelect({
  serviceKind,
  value,
  onValueChange,
  invalid,
  disabled,
}: {
  serviceKind: string;
  value: string;
  onValueChange: (serviceVariation: ServiceVariation) => void;
  invalid?: boolean;
  disabled?: boolean;
}) {
  const items = serviceVariationSelectItems(serviceKind);

  return (
    <Select
      value={serviceVariationToSelectValue(value)}
      onValueChange={(v) => onValueChange(serviceVariationFromSelectValue(v))}
      disabled={disabled || items.length <= 1}
    >
      <SelectTrigger aria-invalid={invalid || undefined} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

/**
 * ServiceVariationSelectField is the labelled {@link ServiceVariationSelect} of
 * a form, labelled "Variation" unless label is given. It shows and resets the errors
 * of fieldName.
 */
export function ServiceVariationSelectField({
  serviceKind,
  value,
  onValueChange,
  fieldName,
  label = t("Variation"),
  description,
  disabled,
}: {
  serviceKind: string;
  value: string;
  onValueChange: (serviceVariation: ServiceVariation) => void;
  fieldName: string;
  label?: string;
  description?: string;
  disabled?: boolean;
}) {
  const { errors, invalid, reset } = useFieldErrors(fieldName);

  return (
    <Field data-invalid={invalid || undefined}>
      <FieldLabel>{label}</FieldLabel>
      <ServiceVariationSelect
        serviceKind={serviceKind}
        value={value}
        onValueChange={(serviceVariation) => {
          reset();
          onValueChange(serviceVariation);
        }}
        invalid={invalid}
        disabled={disabled}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}
